import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useSearchParams } from "react-router";
import { useAdminApplication, useAdminSession } from "../../app/providers";
import { Alert, Button, DataTable, Field } from "../../ui";
import type { DataColumn } from "../../ui";
import { PageHeader, Panel } from "../workspace/shared";
import { date } from "../workspace/model";
import type { Schemas } from "../workspace/api.generated";
import "./MembershipStatusPage.css";

export type MembershipStatusRow = Schemas["MembershipStatusRow"];
export type MembershipStatusPeriod = Schemas["MembershipStatusPeriod"];
export type MembershipStatusReport = Schemas["MembershipStatusView"];
const number = (value: number) => value.toLocaleString("ko-KR");
type DateRange = { from: string; to: string };
const columns: DataColumn<MembershipStatusRow>[] = [
  { key: "label", title: "가입 채널", render: row => row.key === "TOTAL" ? <strong>{row.label}</strong> : row.label },
  ...([ ["joined", "가입"], ["closed", "해지"], ["maintained", "유지"] ] as const).map(([key, title]) => ({
    key, title, numeric: true, render: (row: MembershipStatusRow) => row.key === "TOTAL" ? <strong>{number(row[key])}</strong> : number(row[key]),
  })),
];

function MembershipDateFilter({ values, max, busy, onSearch, onReset }: {
  values: DateRange; max?: string; busy?: boolean; onSearch?: (range: DateRange) => void; onReset?: () => void;
}) {
  const [from, setFrom] = useState(values.from);
  const [to, setTo] = useState(values.to);
  const [validation, setValidation] = useState("");
  return <Panel title="조회 기간">
    <form className="membership-status__filter" noValidate onSubmit={event => {
      event.preventDefault();
      if (busy) return;
      const message = !from || !to ? "시작일과 종료일을 모두 선택해 주세요." : from > to ? "종료일은 시작일보다 빠를 수 없습니다." : max && to > max ? "오늘 이후의 날짜는 조회할 수 없습니다." : "";
      setValidation(message);
      if (!message) onSearch?.({ from, to });
    }}>
      <Field type="date" label="시작일" value={from} max={to || max} required onChange={event => { setFrom(event.target.value); setValidation(""); }} />
      <Field type="date" label="종료일" value={to} min={from} max={max} required onChange={event => { setTo(event.target.value); setValidation(""); }} />
      <div className="membership-status__filter-actions">
        <Button type="submit" size="small" isLoading={busy}>조회</Button>
        <Button type="button" size="small" variant="secondary" disabled={busy} onClick={onReset}>당일·전일 보기</Button>
      </div>
    </form>
    {validation ? <Alert persistent variant="danger" title={validation} /> : null}
  </Panel>;
}

export function MembershipStatusView({ report, loading, refreshing, error, onRefresh, filters, onSearch, onReset }: {
  report?: MembershipStatusReport; loading?: boolean; refreshing?: boolean; error?: boolean; onRefresh: () => void;
  filters?: DateRange; onSearch?: (range: DateRange) => void; onReset?: () => void;
}) {
  const values = filters ?? { from: report?.today.date ?? "", to: report?.today.date ?? "" };
  return <div className="membership-status">
    <PageHeader id="ADM-S42" title="가입/해지 현황" description="가입 채널별 가입·해지·유지 인원을 확인합니다." />
    <MembershipDateFilter key={`${values.from}:${values.to}`} values={values} max={report?.today.date} busy={refreshing} onSearch={onSearch} onReset={onReset} />
    {report && !error ? <p>집계 시각: {date(report.generatedAt)} (한국시간)</p> : null}
    {loading ? <Alert persistent variant="information" title="현황을 불러오고 있습니다">가입·해지·유지 인원을 조회하고 있습니다.</Alert> : error ? <Alert persistent variant="danger" title="현황을 불러오지 못했습니다" action={<Button size="small" variant="secondary" onClick={onRefresh}>다시 조회</Button>}>연결과 조회 권한을 확인한 뒤 다시 조회해 주세요.</Alert> : report ? <>
      {report.selectedPeriod ? <>
        <p>가입·해지는 선택 기간의 완료 인원입니다. 유지는 종료일 종료 시점의 미해지 인원이며, 종료일이 오늘이면 조회 시점으로 집계합니다.</p>
        <Panel title={`기간별 가입/해지 현황 (${report.selectedPeriod.from} ~ ${report.selectedPeriod.to})`}>
          <DataTable columns={columns} rows={[...report.selectedPeriod.rows, report.selectedPeriod.total]} rowKey={row => row.key} caption="선택 기간 가입 채널별 가입·해지·유지 인원" />
        </Panel>
      </> : <>
      <p>가입·해지는 해당 날짜의 완료 건수입니다. 유지는 당일 조회 시점, 전일 종료 시점의 미해지 인원입니다.</p>
      {([ ["당일", report.today], ["전일", report.yesterday] ] as const).map(([label, period]) => <Panel key={label} title={`${label} 가입/해지 현황 (${period.date})`}>
        <DataTable columns={columns} rows={[...period.rows, period.total]} rowKey={row => row.key} caption={`${label} 가입 채널별 가입·해지·유지 인원`} />
      </Panel>)}
      </>}
    </> : null}
    <Panel title="집계 기준">
      <ul className="membership-status__criteria">
        <li><strong>집계 대상:</strong> 일반가입과 제휴가입을 포함한 전체 회원을 가입 당시 채널별로 집계합니다.</li>
        <li><strong>집계 시간:</strong> 한국시간 기준으로 시작일 00시부터 종료일 24시 직전까지 집계합니다. 종료일이 오늘이면 현재 조회 시점까지 집계합니다. 기본 화면은 당일·전일 현황입니다.</li>
        <li><strong>가입:</strong> 선택한 기간에 가입이 완료된 인원입니다.</li>
        <li><strong>해지:</strong> 선택한 기간에 해지가 완료된 인원입니다. 해지 처리 중인 회원은 해지 완료 인원에 포함하지 않습니다.</li>
        <li><strong>유지:</strong> 종료일 종료 시점에 가입이 완료되고 해지가 완료되지 않은 전체 인원입니다. 종료일이 오늘이면 현재 조회 시점을 기준으로 합니다.</li>
        <li><strong>가입 채널:</strong> 회원에 저장된 가입 채널명·코드로 구분합니다. 같은 채널에서 결제수단이 달라도 한 행으로 집계하며, 정기결제·추가결제로 인원을 중복 집계하지 않습니다. 채널 정보가 없는 회원은 가입 채널 미확인으로 집계합니다. 비활성 채널의 기존 가입·해지 이력도 포함합니다.</li>
      </ul>
    </Panel>
  </div>;
}

export function MembershipStatusPage() {
  const { apiClient } = useAdminApplication();
  const { sessionEpoch } = useAdminSession();
  const [params, setParams] = useSearchParams();
  const from = params.get("from") || undefined;
  const to = params.get("to") || undefined;
  const query = useQuery({
    queryKey: ["admin", sessionEpoch, "membership-status", from, to],
    queryFn: async ({ signal }) => (await apiClient.get<MembershipStatusReport>("/api/v1/admin/bo/membership-status", { signal, query: { from, to }, cache: "no-store" })).data,
    retry: false, refetchOnWindowFocus: false,
  });
  return <MembershipStatusView report={query.data} loading={query.isPending} refreshing={query.isFetching} error={query.isError}
    filters={from || to ? { from: from ?? "", to: to ?? "" } : undefined}
    onSearch={range => {
      if (range.from === from && range.to === to) { void query.refetch(); }
      else setParams(range);
    }} onReset={() => setParams({})} onRefresh={() => { void query.refetch(); }} />;
}
